import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Loader2, ShipWheel } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { apiClient } from '../api/client';
import kyvernoIcon from '../assets/icons/kyverno.svg';

export default function KyvernoIntegration() {
  const [clusters, setClusters] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const result = await apiClient.getKyvernoClusters();
        setClusters(result);
      } catch { /* ignore */ } finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const connected = clusters.length > 0;

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <img src={kyvernoIcon} alt="Kyverno" className="h-8 w-8 object-contain" />
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2">
                  Kyverno
                  {connected && (
                    <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-normal">Detected</Badge>
                  )}
                </h1>
                <p className="text-sm text-muted-foreground">Kubernetes policy-as-code: react to policy violations and admission blocks, and manage guardrails from workflows</p>
              </div>
            </div>
          </div>

          {connected ? (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Kyverno was detected on the following clusters. Kyverno triggers and actions are available in the workflow builder.
              </p>
              <div className="flex flex-wrap gap-2">
                {clusters.map(c => (
                  <div key={c.id} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border bg-muted/30">
                    <ShipWheel className="w-4 h-4 text-foreground shrink-0" />
                    <span className="text-sm font-medium">{c.name}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Kyverno is detected automatically by the Kestrel operator. Install Kyverno in a cluster
                where the operator is running and the integration will activate automatically
                — no credentials required.
              </p>
              <div className="p-3 rounded-lg border bg-muted/30">
                <p className="text-sm font-medium mb-1">Requirements</p>
                <ul className="text-xs text-muted-foreground list-disc pl-4 space-y-1">
                  <li>Kestrel operator installed in the cluster</li>
                  <li>Kyverno controller and CRDs installed (clusterpolicies.kyverno.io, policies.kyverno.io)</li>
                </ul>
              </div>
              <Button asChild variant="outline" size="sm">
                <Link to="/onboard-cluster">Onboard a cluster</Link>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
